import * as React from 'react'

import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { useResource } from './context'

/**
 * Delete action for the resource edit card's 3-dot overflow menu. Returns an
 * `onDelete` to hand to the card and a `confirmDialog` node to render: the dialog
 * confirms, soft-deletes the record via the Resource provider, and closes the
 * panel. Used by the desktop Equipment / Materials / StruxTags edit panels.
 */
export function useResourceDeleteAction(recordId: string | null): {
  onDelete: () => void
  confirmDialog: React.ReactNode
} {
  const { actions } = useResource()
  const [isOpen, setIsOpen] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const onDelete = React.useCallback(() => setIsOpen(true), [])

  const handleDelete = React.useCallback(async () => {
    if (!recordId || recordId === 'new') return
    setIsDeleting(true)
    try {
      await actions.deleteRecords([recordId])
      actions.closeEditPanel()
    } finally {
      setIsDeleting(false)
      setIsOpen(false)
    }
  }, [recordId, actions])

  const confirmDialog = (
    <ConfirmDialog
      useRedConfirmationButton
      title={Localization.translator.deleteResource}
      isOpen={isOpen}
      loading={isDeleting}
      onCancelClicked={() => setIsOpen(false)}
      onDoneClicked={handleDelete}
      doneTitle={Localization.translator.delete}
    >
      <div className="tw:text-body-md tw:whitespace-pre-line tw:pb-2.5">
        {Localization.translator.deleteResourceQuestion}
      </div>
    </ConfirmDialog>
  )

  return { onDelete, confirmDialog }
}
